import { useState, useEffect, useRef } from 'react'
import { useLanguage } from '../context/LanguageContext'

interface FilterPanelProps {
  isOpen: boolean
  onClose: () => void
  selectedApps: string[]
  selectedActions: string[]
  onApply: (apps: string[], actions: string[]) => void
  availableApps: string[]
  availableActions: string[]
}

export default function FilterPanel({
  isOpen,
  onClose,
  selectedApps,
  selectedActions,
  onApply,
  availableApps,
  availableActions,
}: FilterPanelProps) {
  const [tempApps, setTempApps] = useState<string[]>(selectedApps)
  const [tempActions, setTempActions] = useState<string[]>(selectedActions)
  const [appsDropdownOpen, setAppsDropdownOpen] = useState(false)
  const [actionsDropdownOpen, setActionsDropdownOpen] = useState(false)
  const [searchApps, setSearchApps] = useState('')
  const [searchActions, setSearchActions] = useState('')
  const appsDropdownRef = useRef<HTMLDivElement>(null)
  const actionsDropdownRef = useRef<HTMLDivElement>(null)
  const { t } = useLanguage()

  useEffect(() => {
    if (isOpen) {
      // Use exactly what's selected (don't auto-fill)
      setTempApps(selectedApps)
      setTempActions(selectedActions)
      setSearchApps('')
      setSearchActions('')
    }
  }, [isOpen, selectedApps, selectedActions])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (appsDropdownRef.current && !appsDropdownRef.current.contains(event.target as Node)) {
        setAppsDropdownOpen(false)
      }
      if (actionsDropdownRef.current && !actionsDropdownRef.current.contains(event.target as Node)) {
        setActionsDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleReset = () => {
    setTempApps([])
    setTempActions([])
    setSearchApps('')
    setSearchActions('')
    onApply([], [])
  }

  const toggleApp = (app: string) => {
    const newApps = tempApps.includes(app)
      ? tempApps.filter(a => a !== app)
      : [...tempApps, app]
    setTempApps(newApps)
    onApply(newApps, tempActions)
  }

  const toggleAction = (action: string) => {
    const newActions = tempActions.includes(action)
      ? tempActions.filter(a => a !== action)
      : [...tempActions, action]
    setTempActions(newActions)
    onApply(tempApps, newActions)
  }

  const selectAllApps = () => {
    setTempApps(availableApps)
    onApply(availableApps, tempActions)
  }

  const deselectAllApps = () => {
    setTempApps([])
    onApply([], tempActions)
  }

  const selectAllActions = () => {
    setTempActions(availableActions)
    onApply(tempApps, availableActions)
  }

  const deselectAllActions = () => {
    setTempActions([])
    onApply(tempApps, [])
  }

  const isAllAppsSelected = tempApps.length === availableApps.length
  const isAllActionsSelected = tempActions.length === availableActions.length
  const isNoAppsSelected = tempApps.length === 0
  const isNoActionsSelected = tempActions.length === 0

  const filteredApps = availableApps
    .filter(app => app.toLowerCase().includes(searchApps.toLowerCase()))
    .sort((a, b) => a.localeCompare(b))

  const filteredActions = availableActions
    .filter(action => action.toLowerCase().includes(searchActions.toLowerCase()))
    .sort((a, b) => a.localeCompare(b))

  // Smart labels for dropdown buttons
  const getAppsLabel = () => {
    if (tempApps.length === 0) return t.filters.noAppsSelected
    if (tempApps.length === availableApps.length) return t.filters.all
    if (tempApps.length === 1) return tempApps[0]
    return `${tempApps.length} Apps`
  }

  const getActionsLabel = () => {
    if (tempActions.length === 0) return t.filters.noActionsSelected
    if (tempActions.length === availableActions.length) return t.filters.all
    if (tempActions.length === 1) return tempActions[0]
    return `${tempActions.length} ${t.filters.actions}`
  }

  const hasFilters = tempApps.length > 0 || tempActions.length > 0
  const totalFilters = tempApps.length + tempActions.length

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 backdrop-blur-sm"
          onClick={onClose}
        />
      )}

      {/* Sidebar Panel */}
      <div
        className={`fixed top-0 right-0 h-full w-full md:w-[450px] bg-white shadow-2xl z-50 transform transition-transform duration-300 overflow-y-auto ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <span>🔍</span> {t.filters.title}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-3xl leading-none"
            >
              ×
            </button>
          </div>

          {/* Apps Filter Section */}
          <div className="mb-6" ref={appsDropdownRef}>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xl">📱</span>
              <h3 className="text-lg font-bold text-gray-800">{t.filters.apps}</h3>
              {tempApps.length > 0 && (
                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-bold">
                  {tempApps.length}
                </span>
              )}
            </div>

            {/* Dropdown Button */}
            <button
              onClick={() => setAppsDropdownOpen(!appsDropdownOpen)}
              className="w-full px-4 py-3 bg-white border-2 border-gray-300 rounded-xl hover:border-blue-400 transition-all flex items-center justify-between group"
            >
              <span className="text-sm text-gray-600 group-hover:text-blue-600 font-medium truncate pr-2">
                {getAppsLabel()}
              </span>
              <svg className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ${appsDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* Dropdown Menu */}
            {appsDropdownOpen && (
              <div className="mt-2 bg-white border-2 border-blue-200 rounded-xl shadow-xl overflow-hidden">
                {/* Search */}
                <div className="p-3 border-b-2 border-gray-100">
                  <input
                    type="text"
                    placeholder={t.filters.searchApps}
                    value={searchApps}
                    onChange={(e) => setSearchApps(e.target.value)}
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                {/* All / None */}
                <div className="px-3 py-2 border-b-2 border-gray-100 flex gap-2">
                  <button
                    onClick={selectAllApps}
                    className={`flex-1 px-3 py-1.5 text-xs font-bold rounded-lg transition-colors ${
                      isAllAppsSelected
                        ? 'text-white bg-blue-600 hover:bg-blue-700'
                        : 'text-blue-700 bg-blue-50 hover:bg-blue-100'
                    }`}
                  >
                    ✓ {t.filters.all}
                  </button>
                  <button
                    onClick={deselectAllApps}
                    className={`flex-1 px-3 py-1.5 text-xs font-bold rounded-lg transition-colors ${
                      isNoAppsSelected
                        ? 'text-white bg-gray-600 hover:bg-gray-700'
                        : 'text-gray-700 bg-gray-50 hover:bg-gray-100'
                    }`}
                  >
                    ✗ {t.filters.none}
                  </button>
                </div>
                {/* Options */}
                <div className="max-h-96 overflow-y-auto border-t border-gray-100">
                  {filteredApps.length === 0 ? (
                    <p className="text-sm text-gray-500 italic py-4 px-4">{t.filters.noAppsFound}</p>
                  ) : (
                    <>
                      {filteredApps.map(app => (
                        <label
                          key={app}
                          className="flex items-center gap-3 px-4 py-3 hover:bg-gradient-to-r hover:from-yellow-50 hover:to-amber-50 cursor-pointer transition-all border-l-4 border-transparent hover:border-yellow-500"
                        >
                          <input
                            type="checkbox"
                            checked={tempApps.includes(app)}
                            onChange={() => toggleApp(app)}
                            className="w-5 h-5 text-blue-600 rounded-md focus:ring-2 focus:ring-blue-500 cursor-pointer"
                          />
                          <span className="text-sm font-medium text-gray-700">
                            {app}
                          </span>
                        </label>
                      ))}
                      {filteredApps.length > 8 && (
                        <div className="px-4 py-2 text-xs text-gray-500 text-center border-t border-gray-100 bg-gray-50">
                          {t.filters.appsScrollMore.replace('{n}', String(filteredApps.length))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Actions Filter Section */}
          <div className="mb-6" ref={actionsDropdownRef}>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xl">⚡</span>
              <h3 className="text-lg font-bold text-gray-800">{t.filters.actions}</h3>
              {tempActions.length > 0 && (
                <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full text-xs font-bold">
                  {tempActions.length}
                </span>
              )}
            </div>

            {/* Dropdown Button */}
            <button
              onClick={() => setActionsDropdownOpen(!actionsDropdownOpen)}
              className="w-full px-4 py-3 bg-white border-2 border-gray-300 rounded-xl hover:border-yellow-400 transition-all flex items-center justify-between group"
            >
              <span className="text-sm text-gray-600 group-hover:text-yellow-600 font-medium truncate pr-2">
                {getActionsLabel()}
              </span>
              <svg className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ${actionsDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* Dropdown Menu */}
            {actionsDropdownOpen && (
              <div className="mt-2 bg-white border-2 border-yellow-200 rounded-xl shadow-xl overflow-hidden">
                {/* Search */}
                <div className="p-3 border-b-2 border-gray-100">
                  <input
                    type="text"
                    placeholder={t.filters.searchActions}
                    value={searchActions}
                    onChange={(e) => setSearchActions(e.target.value)}
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  />
                </div>
                {/* All / None */}
                <div className="px-3 py-2 border-b-2 border-gray-100 flex gap-2">
                  <button
                    onClick={selectAllActions}
                    className={`flex-1 px-3 py-1.5 text-xs font-bold rounded-lg transition-colors ${
                      isAllActionsSelected
                        ? 'text-gray-900 bg-yellow-500 hover:bg-yellow-600'
                        : 'text-yellow-700 bg-yellow-50 hover:bg-yellow-100'
                    }`}
                  >
                    ✓ {t.filters.all}
                  </button>
                  <button
                    onClick={deselectAllActions}
                    className={`flex-1 px-3 py-1.5 text-xs font-bold rounded-lg transition-colors ${
                      isNoActionsSelected
                        ? 'text-white bg-gray-600 hover:bg-gray-700'
                        : 'text-gray-700 bg-gray-50 hover:bg-gray-100'
                    }`}
                  >
                    ✗ {t.filters.none}
                  </button>
                </div>
                {/* Options */}
                <div className="max-h-96 overflow-y-auto border-t border-gray-100">
                  {filteredActions.length === 0 ? (
                    <p className="text-sm text-gray-500 italic py-4 px-4">{t.filters.noActionsFound}</p>
                  ) : (
                    <>
                      {filteredActions.map(action => (
                        <label
                          key={action}
                          className="flex items-center gap-3 px-4 py-3 hover:bg-gradient-to-r hover:from-yellow-50 hover:to-amber-50 cursor-pointer transition-all border-l-4 border-transparent hover:border-yellow-500"
                        >
                          <input
                            type="checkbox"
                            checked={tempActions.includes(action)}
                            onChange={() => toggleAction(action)}
                            className="w-5 h-5 text-yellow-600 rounded-md focus:ring-2 focus:ring-yellow-500 cursor-pointer flex-shrink-0"
                          />
                          <span className="text-sm font-medium text-gray-700">
                            {action}
                          </span>
                        </label>
                      ))}
                      {filteredActions.length > 8 && (
                        <div className="px-4 py-2 text-xs text-gray-500 text-center border-t border-gray-100 bg-gray-50">
                          {t.filters.actionsScrollMore.replace('{n}', String(filteredActions.length))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            {hasFilters && (
              <button
                onClick={handleReset}
                className="flex-1 px-4 py-4 text-sm font-bold text-gray-700 bg-white border-2 border-gray-300 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all"
              >
                🗑️ {t.filters.resetAll}
              </button>
            )}
            <button
              onClick={onClose}
              className="flex-1 px-4 py-4 text-sm font-bold text-[#1A1A1A] bg-[#FBB300] hover:bg-[#F29100] rounded-xl hover:shadow-lg transition-all"
            >
              ✓ {t.filters.done} {hasFilters && `(${totalFilters} ${t.filters.active})`}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}
